import React from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { FiEye, FiPaperclip } from "react-icons/fi";

import type { DepartmentReportMediaItem } from "@core/reports/api/types";
import { formatFileSize } from "@core/reports/constants/mediaAttachments";

export type ReportMediaDetailsModalProps = {
  show: boolean;
  onHide: () => void;
  /** Modal title. Defaults to "Attachments". */
  title?: string;
  /** Optional subtitle under the title (e.g. church or district name). */
  contextLabel?: string;
  items: DepartmentReportMediaItem[];
  onViewFile: (item: DepartmentReportMediaItem) => void | Promise<void>;
  viewingId?: number | null;
};

function formatUploadedAt(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function FileMeta({ media }: { media: DepartmentReportMediaItem }) {
  const when = formatUploadedAt(media.created_at);
  const who = media.uploaded_by;
  if (!who && !when) return null;
  return (
    <div className="text-muted small">
      {who ? `Uploaded by ${who}` : "Uploaded"}
      {when ? ` · ${when}` : ""}
    </div>
  );
}

/**
 * Read-only modal listing full attachment metadata (caption, filename, upload meta).
 * Presentational only — the parent owns open-file / auth behavior via onViewFile.
 */
export default function ReportMediaDetailsModal({
  show,
  onHide,
  title = "Attachments",
  contextLabel,
  items,
  onViewFile,
  viewingId = null,
}: ReportMediaDetailsModalProps) {
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {contextLabel ? <div className="text-muted small mb-3">{contextLabel}</div> : null}

        <div className="d-flex flex-column gap-3">
          {items.map((item, index) => {
            const caption = item.description?.trim() ?? "";
            const typeLabel = item.document_name?.trim() || "Other";
            const fileLabel = item.filename || "Attachment";
            return (
              <div
                key={`${item.media_storage_id}-${index}`}
                className="border rounded px-3 py-3"
              >
                <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                  <div className="d-flex align-items-start gap-2 min-width-0">
                    <FiPaperclip className="text-muted flex-shrink-0 mt-1" aria-hidden />
                    <div className="fw-medium">{typeLabel}</div>
                  </div>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    className="flex-shrink-0"
                    onClick={() => onViewFile(item)}
                    disabled={viewingId !== null}
                    title="Open file"
                    aria-label={`Open ${fileLabel}`}
                  >
                    {viewingId === item.media_storage_id ? (
                      <Spinner animation="border" size="sm" aria-hidden />
                    ) : (
                      <>
                        <FiEye className="me-1" aria-hidden />
                        Open file
                      </>
                    )}
                  </Button>
                </div>

                <div
                  className="mb-2"
                  style={{ wordBreak: "break-word", lineHeight: 1.4 }}
                >
                  {fileLabel}
                  <div className="text-muted small">
                    {formatFileSize(item.size_bytes)}
                    {item.mime_type ? ` · ${item.mime_type}` : ""}
                  </div>
                </div>

                {caption ? (
                  <div className="mb-2">
                    <div className="text-muted small mb-1">Caption</div>
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        lineHeight: 1.5,
                      }}
                    >
                      {caption}
                    </div>
                  </div>
                ) : null}

                <FileMeta media={item} />
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
